import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import store from "store";
import { ChildContainerProps } from "../types";

export type SidebarDesktopMode = "expanded" | "mini";

const STORAGE_KEY = `${process.env.REACT_APP_STORAGE_PREFIX}.sidebarMode`;
const MOBILE_BREAKPOINT = 992;

interface SidebarContextValue {
    desktopMode: SidebarDesktopMode;
    mobileOpen: boolean;
    isMobile: boolean;
    isMini: boolean;
    isExpanded: boolean;
    toggleSidebar: () => void;
    closeMobile: () => void;
}

export const SidebarContext = createContext({} as SidebarContextValue);

export const SidebarProvider = ({ children }: ChildContainerProps) => {
    const [desktopMode, setDesktopMode] = useState<SidebarDesktopMode>(() => {
        const stored = store.get(STORAGE_KEY);
        return stored === "mini" ? "mini" : "expanded";
    });
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(
        () => window.innerWidth < MOBILE_BREAKPOINT
    );

    useEffect(() => {
        const onResize = () => {
            const mobile = window.innerWidth < MOBILE_BREAKPOINT;
            setIsMobile(mobile);

            if (!mobile) {
                setMobileOpen(false);
            }
        };

        onResize();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        store.set(STORAGE_KEY, desktopMode);
    }, [desktopMode]);

    const toggleSidebar = useCallback(() => {
        if (window.innerWidth < MOBILE_BREAKPOINT) {
            setMobileOpen((open) => !open);
            return;
        }

        setDesktopMode((mode) => (mode === "expanded" ? "mini" : "expanded"));
    }, []);

    const closeMobile = useCallback(() => {
        setMobileOpen(false);
    }, []);

    const value = useMemo(
        () => ({
            desktopMode,
            mobileOpen,
            isMobile,
            isMini: !isMobile && desktopMode === "mini",
            isExpanded: !isMobile && desktopMode === "expanded",
            toggleSidebar,
            closeMobile,
        }),
        [closeMobile, desktopMode, isMobile, mobileOpen, toggleSidebar]
    );

    return (
        <SidebarContext.Provider value={value}>
            {children}
        </SidebarContext.Provider>
    );
};

export const useSidebar = () => {
    const context = useContext(SidebarContext);

    if (!context.toggleSidebar) {
        throw new Error("useSidebar must be used within SidebarProvider");
    }

    return context;
};
